import { useMutation, useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import GeoPointsList from "@/components/geo/GeoPointsList"
import { normalizeGeoPoint } from "@/components/geo/geoPointHelpers"
import { usePlayerGpsState } from "@/hooks/usePlayerGpsState"
import {
  claimWiseTree,
  getWiseTreePoints,
  getWiseTreeStatus,
} from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"

export default function WiseTreePage() {
  const [params] = useSearchParams()
  const pointId = params.get("pointId")
  const gps = usePlayerGpsState()

  const pointsQ = useQuery({
    queryKey: ["wise-tree-points"],
    queryFn: getWiseTreePoints,
  })

  const statusQ = useQuery({
    queryKey: ["wise-tree-status", pointId],
    queryFn: () => getWiseTreeStatus(pointId ?? undefined),
    enabled: Boolean(pointId),
  })

  const claim = useMutation({
    mutationFn: (id: string) => {
      if (!gps) throw new Error("GPS")
      return claimWiseTree({
        pointId: id,
        playerLat: gps.lat,
        playerLng: gps.lng,
      })
    },
    onSuccess: () => void statusQ.refetch(),
  })

  const points = (pointsQ.data ?? []).map((p) =>
    normalizeGeoPoint({
      id: p.id,
      name: p.name,
      latitude: p.latitude,
      longitude: p.longitude,
      radiusMeters: p.radiusMeters,
    })
  )

  return (
    <GameScreenLayout title="Мудрое дерево" backTo="/play/map">
      {statusQ.data ? (
        <p className="mb-3 text-xs text-guardians-muted">
          Оккупантов: {statusQ.data.occupants?.length ?? 0}
        </p>
      ) : null}
      <GeoPointsList
        points={points}
        gps={gps}
        highlightId={pointId}
        renderActions={({ point, inRange }) => (
          <button
            type="button"
            disabled={!inRange || claim.isPending}
            onClick={() => claim.mutate(point.id)}
            className="rounded-lg bg-guardians-accent px-3 py-1.5 text-xs text-guardians-bg disabled:opacity-40"
          >
            Награда
          </button>
        )}
      />
      {claim.isError ? (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(claim.error, "Ошибка")}
        </p>
      ) : null}
    </GameScreenLayout>
  )
}
